import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { IconSearch, IconGrid, IconEdit, IconTrash } from './Icons';
import './SalaryChart.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const salaryChartSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const INITIAL_FORM = {
  employee_id: '',
  employee_name: '',
  designation: '',
  salary: '',
  allowance: '',
  pasi_percent: '8',
};

function calcTotal(salary, allowance, pasiPercent) {
  const s = Number(salary) || 0;
  const a = Number(allowance) || 0;
  const p = Number(pasiPercent) || 0;
  return (s + a) * (1 - p / 100);
}

function toTableRow(row, index) {
  const displayName =
    row.profiles?.full_name ?? row.employee_name ?? '';
  const designation =
    row.profiles?.designation ?? row.profiles?.Designation ?? row.designation ?? '';
  const salary = Number(row.salary) || 0;
  const allowance = Number(row.allowance) || 0;
  const pasiPercent = Number(row.pasi_percent) || 0;
  const total = row.total != null ? Number(row.total) : calcTotal(salary, allowance, pasiPercent);
  return {
    id: row.id,
    employeeId: row.employee_id ?? null,
    srNo: index + 1,
    employee: displayName,
    designation,
    salary,
    allowance,
    pasiPercent,
    total,
  };
}

export default function SalaryChart() {
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editForm, setEditForm] = useState({
    id: null,
    employeeId: null,
    employee: '',
    designation: '',
    salary: '',
    allowance: '',
    pasi_percent: '8',
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [editError, setEditError] = useState(null);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      salaryChartSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchProfiles = useCallback(async () => {
    setProfilesError(null);
    let result = await salaryChartSupabase
      .from('profiles')
      .select('id, full_name, "Designation"')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (result.error) {
      result = await salaryChartSupabase
        .from('profiles')
        .select('id, full_name')
        .order('full_name', { ascending: true, nullsFirst: false });
    }
    if (result.error) {
      setProfilesError(result.error.message);
      setProfiles([]);
      return;
    }
    setProfiles(result.data ?? []);
  }, []);

  const fetchSalaries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        salaryChartSupabase
          .from('salary_chart')
          .select('*, profiles(full_name, "Designation")')
          .order('created_at', { ascending: false }),
        timeoutPromise,
      ]);
      const { data, error: e } = res;
      if (e) {
        setError(e.message);
        setRows([]);
      } else {
        setRows(data ?? []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    setError(null);
    setProfilesError(null);
    try {
      await salaryChartSupabase.auth.getSession();
      await runWakeUp();
      await Promise.all([fetchSalaries(), fetchProfiles()]);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchSalaries, fetchProfiles]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await salaryChartSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await Promise.all([fetchSalaries(), fetchProfiles()]);
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchSalaries, fetchProfiles]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const existingEmployeeIds = useMemo(() => {
    return new Set((rows ?? []).map((r) => r.employee_id).filter(Boolean));
  }, [rows]);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const selectedId = form.employee_id?.trim() || null;
    if (!selectedId) {
      setFormError('Please select an employee.');
      return;
    }
    if (existingEmployeeIds.has(selectedId)) {
      setFormError('A salary row already exists for this employee.');
      return;
    }
    setSaving(true);
    try {
      await runWakeUp();
      const selectedProfile = profiles.find((p) => p.id === selectedId);
      const salary = Number(form.salary) || 0;
      const allowance = Number(form.allowance) || 0;
      const pasiPercent = 8;
      const total = calcTotal(salary, allowance, pasiPercent);
      const payload = {
        employee_id: selectedId,
        employee_name: selectedProfile?.full_name?.trim() || form.employee_name.trim() || null,
        designation: (selectedProfile?.designation ?? selectedProfile?.Designation)?.trim() || null,
        salary,
        allowance,
        pasi_percent: pasiPercent,
        total,
      };
      const { error: insertError } = await salaryChartSupabase.from('salary_chart').insert(payload);
      if (insertError) {
        if (insertError.code === '23505') {
          setFormError('A salary row already exists for this employee.');
        } else {
          setFormError(insertError.message);
        }
        return;
      }
      setForm(INITIAL_FORM);
      setShowAddModal(false);
      setError(null);
      fetchSalaries();
    } catch (err) {
      setFormError(err?.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const tableRows = rows.map((row, i) => toTableRow(row, i));
  const searchLower = search.trim().toLowerCase();
  const filteredRows =
    searchLower === ''
      ? tableRows
      : tableRows.filter(
          (r) =>
            (r.employee && r.employee.toLowerCase().includes(searchLower)) ||
            (r.designation && r.designation.toLowerCase().includes(searchLower))
        );

  const COLUMNS = [
    { key: 'srNo', label: 'Sr.No' },
    { key: 'employee', label: 'Employee' },
    { key: 'designation', label: 'Designation' },
    { key: 'salary', label: 'Salary' },
    { key: 'allowance', label: 'Allowance' },
    { key: 'pasiPercent', label: 'PASI (%)' },
    { key: 'total', label: 'Total' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    COLUMNS.reduce(
      (acc, col) => ({
        ...acc,
        [col.key]: true,
      }),
      {},
    ),
  );

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const columns = COLUMNS.filter((col) => visibleColumns[col.key] !== false);
  const activeColumnCount = columns.length + 1; // +1 for actions column

  const handleExportExcel = () => {
    const header = COLUMNS.map((c) => c.label);
    const rows = filteredRows.map((row) =>
      COLUMNS.map((col) => {
        const v = row[col.key];
        if (v === undefined || v === null) return '';
        if (typeof v === 'number') return String(v);
        return String(v);
      }),
    );
    const csvLines = [header, ...rows].map((cols) =>
      cols
        .map((v) => {
          const s = v == null ? '' : String(v);
          return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'salary-chart-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEditModal = (row) => {
    setEditError(null);
    setShowEditModal(true);
    setEditForm({
      id: row.id,
      employeeId: row.employeeId,
      employee: row.employee,
      designation: row.designation,
      salary: String(row.salary ?? ''),
      allowance: String(row.allowance ?? ''),
      pasi_percent: '8',
    });
  };

  const closeEditModal = () => {
    if (saving) return;
    setShowEditModal(false);
    setEditError(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.id) return;
    setEditError(null);
    setSaving(true);
    try {
      await runWakeUp();
      const salary = Number(editForm.salary) || 0;
      const allowance = Number(editForm.allowance) || 0;
      const pasiPercent = 8;
      const total = calcTotal(salary, allowance, pasiPercent);
      const { error: updateError } = await salaryChartSupabase
        .from('salary_chart')
        .update({
          salary,
          allowance,
          pasi_percent: pasiPercent,
          total,
          employee_name: editForm.employee?.trim() || null,
          designation: editForm.designation?.trim() || null,
        })
        .eq('id', editForm.id);
      if (updateError) {
        setEditError(updateError.message);
        return;
      }
      setShowEditModal(false);
      fetchSalaries();
    } catch (err) {
      setEditError(err?.message || 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm('Delete this salary row?')) return;
    setDeletingId(row.id);
    try {
      await runWakeUp();
      const { error: delErr } = await salaryChartSupabase.from('salary_chart').delete().eq('id', row.id);
      if (delErr) throw delErr;
      fetchSalaries();
    } catch (e) {
      setError(e?.message || 'Failed to delete.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="salary-page">
      <div className="salary-card">
        <div className="salary-header-row">
          <h1 className="salary-title">Salary Summary</h1>

          <div className="salary-header-right">
            <button
              type="button"
              className="salary-utility-btn salary-add-btn"
              onClick={() => {
                setError(null);
                setFormError(null);
                setForm(INITIAL_FORM);
                setShowAddModal(true);
              }}
            >
              Add salary
            </button>
            <button type="button" className="salary-utility-btn" onClick={handleExportExcel} disabled={loading || filteredRows.length === 0}>
              Excel
            </button>
            <div className="salary-search">
              <input
                type="text"
                placeholder="Search"
                aria-label="Search salary"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="salary-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        <div className="salary-filters-row">
          <div className="salary-filters-left">
            <button
              type="button"
              className="salary-layout-toggle"
              aria-label="Layout options"
              onClick={() => setColumnMenuOpen((open) => !open)}
            >
              <IconGrid />
            </button>

            {columnMenuOpen && (
              <div className="salary-column-menu">
                {COLUMNS.map((col) => (
                  <button
                    key={col.key}
                    type="button"
                    className={`salary-column-menu-item${
                      visibleColumns[col.key] !== false ? ' active' : ''
                    }`}
                    onClick={() => toggleColumn(col.key)}
                  >
                    <span>{col.label}</span>
                    {visibleColumns[col.key] !== false && (
                      <span className="salary-column-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="salary-table-wrapper">
          <table className="salary-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="salary-table-empty" colSpan={activeColumnCount}>
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className="salary-table-empty" colSpan={activeColumnCount}>
                    {error}
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td className="salary-table-empty" colSpan={activeColumnCount}>
                    No record found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((col) => (
                      <td key={col.key}>
                        {col.key === 'pasiPercent' && typeof row[col.key] === 'number'
                          ? `${Number(row[col.key]).toLocaleString()}%`
                          : typeof row[col.key] === 'number' && col.key !== 'srNo'
                            ? Number(row[col.key]).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : row[col.key]}
                      </td>
                    ))}
                    <td className="salary-actions-cell">
                      <button
                        type="button"
                        className="salary-row-btn"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() => openEditModal(row)}
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="salary-row-btn salary-row-btn-danger"
                        title="Delete"
                        aria-label="Delete"
                        onClick={() => handleDelete(row)}
                        disabled={deletingId === row.id}
                      >
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="salary-footer-row">
          <div className="salary-footer-left">
            <select
              className="salary-page-size"
              defaultValue="10"
              aria-label="Rows per page"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>0-0 / 0</span>
          </div>
          <div className="salary-footer-right">
            <button
              type="button"
              className="salary-page-btn"
              aria-label="Previous page"
            >
              «
            </button>
            <button
              type="button"
              className="salary-page-btn"
              aria-label="Next page"
            >
              »
            </button>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => !saving && setShowAddModal(false)}
        >
          <div
            className="estimates-modal salary-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Add employee salary</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => !saving && setShowAddModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form id="salary-add-form" onSubmit={handleAddSubmit}>
              <div className="estimates-modal-body">
                <div className="salary-form-grid">
                <div className="estimates-form-label">Employee <span className="salary-required">*</span></div>
                <div>
                  {profilesError && (
                    <p className="salary-form-error" role="alert">
                      {profilesError}
                    </p>
                  )}
                  {!profilesError && profiles.length === 0 && (
                    <p className="salary-form-error" role="alert">
                      No users in list. Be sure you’re logged in and run{' '}
                      <code>004_profiles_authenticated_select.sql</code> in Supabase (SQL Editor) so
                      profiles can be listed.
                    </p>
                  )}
                  <select
                    className="estimates-form-input"
                    value={form.employee_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const profile = profiles.find((p) => p.id === id);
                      setForm((f) => ({
                        ...f,
                        employee_id: id,
                        employee_name: profile?.full_name ?? f.employee_name,
                      }));
                    }}
                    required
                  >
                    <option value="">Select user…</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id} disabled={existingEmployeeIds.has(p.id)}>
                        {p.full_name || p.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="estimates-form-label">Designation</div>
                <div className="salary-form-readonly" aria-live="polite">
                  {(() => {
                    const p = form.employee_id
                      ? profiles.find((pr) => pr.id === form.employee_id)
                      : null;
                    return p?.designation ?? p?.Designation ?? '—';
                  })()}
                </div>
                <div className="estimates-form-label">Salary</div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="estimates-form-input"
                  placeholder="0"
                  value={form.salary}
                  onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))}
                />
                <div className="estimates-form-label">Allowance</div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="estimates-form-input"
                  placeholder="0"
                  value={form.allowance}
                  onChange={(e) => setForm((f) => ({ ...f, allowance: e.target.value }))}
                />
                <div className="estimates-form-label">PASI (%)</div>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="estimates-form-input"
                    placeholder="8"
                  value={form.pasi_percent}
                    readOnly
                />
                <div className="estimates-form-label">Total</div>
                <div className="salary-form-readonly" aria-live="polite">
                  {calcTotal(form.salary, form.allowance, form.pasi_percent).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}
                </div>
                </div>
              </div>
              {formError && (
                <p className="salary-form-error salary-form-error-inline" role="alert">
                  {formError}
                </p>
              )}
              <div className="estimates-modal-footer">
                <button
                  type="button"
                  className="estimates-footer-btn"
                  onClick={() => !saving && setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="estimates-footer-btn estimates-footer-btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="estimates-modal-backdrop" onClick={closeEditModal}>
          <div className="estimates-modal salary-modal" onClick={(e) => e.stopPropagation()}>
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Edit employee salary</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={closeEditModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form id="salary-edit-form" onSubmit={handleEditSubmit}>
              <div className="estimates-modal-body">
                <div className="salary-form-grid">
                  <div className="estimates-form-label">Employee</div>
                  <div className="salary-form-readonly" aria-live="polite">
                    {editForm.employee || '—'}
                  </div>

                  <div className="estimates-form-label">Designation</div>
                  <div className="salary-form-readonly" aria-live="polite">
                    {editForm.designation || '—'}
                  </div>

                  <div className="estimates-form-label">Salary</div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="estimates-form-input"
                    placeholder="0"
                    value={editForm.salary}
                    onChange={(e) => setEditForm((f) => ({ ...f, salary: e.target.value }))}
                  />

                  <div className="estimates-form-label">Allowance</div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="estimates-form-input"
                    placeholder="0"
                    value={editForm.allowance}
                    onChange={(e) => setEditForm((f) => ({ ...f, allowance: e.target.value }))}
                  />

                  <div className="estimates-form-label">PASI (%)</div>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="estimates-form-input"
                    placeholder="8"
                    value={editForm.pasi_percent}
                    readOnly
                  />

                  <div className="estimates-form-label">Total</div>
                  <div className="salary-form-readonly" aria-live="polite">
                    {calcTotal(editForm.salary, editForm.allowance, editForm.pasi_percent).toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </div>
                </div>
              </div>

              {editError && (
                <p className="salary-form-error salary-form-error-inline" role="alert">
                  {editError}
                </p>
              )}

              <div className="estimates-modal-footer">
                <button type="button" className="estimates-footer-btn" onClick={closeEditModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="estimates-footer-btn estimates-footer-btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

